import React, { useEffect, useState, useRef } from "react";
import { notification } from "antd";
import { FileImageOutlined, TableOutlined, DatabaseOutlined } from "@ant-design/icons";
import { scaleBand, scaleLinear } from "d3-scale";
import { axisBottom, axisLeft } from "d3-axis";
import { select } from "d3-selection";
import { ConditionalTooltip } from "../ConditionalTooltip";

export type BoxPlotStats = {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
    outliers: number[];
};

export type BoxPlotData = {
    title: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    isGrouped: boolean;
    boxes?: Array<{
        category: string;
        color: string;
        values: number[];
        stats: BoxPlotStats;
    }>;
    series?: Array<{
        name: string;
        color: string;
        boxes: Array<{
            category: string;
            values: number[];
            stats: BoxPlotStats;
        }>;
    }>;
};

// Calculate quartiles from sorted array
const calculateQuartiles = (sortedValues: number[]): { q1: number; median: number; q3: number } => {
    const len = sortedValues.length;
    if (len === 0) {
        return { q1: 0, median: 0, q3: 0 };
    }

    // Median (Q2)
    const median = len % 2 === 0
        ? (sortedValues[len / 2 - 1] + sortedValues[len / 2]) / 2
        : sortedValues[Math.floor(len / 2)];

    // Q1 (median of lower half)
    const lowerHalf = sortedValues.slice(0, Math.floor(len / 2));
    const q1 = lowerHalf.length % 2 === 0 && lowerHalf.length > 0
        ? (lowerHalf[lowerHalf.length / 2 - 1] + lowerHalf[lowerHalf.length / 2]) / 2
        : lowerHalf.length > 0
            ? lowerHalf[Math.floor(lowerHalf.length / 2)]
            : sortedValues[0];

    // Q3 (median of upper half)
    const upperHalf = sortedValues.slice(Math.ceil(len / 2));
    const q3 = upperHalf.length % 2 === 0 && upperHalf.length > 0
        ? (upperHalf[upperHalf.length / 2 - 1] + upperHalf[upperHalf.length / 2]) / 2
        : upperHalf.length > 0
            ? upperHalf[Math.floor(upperHalf.length / 2)]
            : sortedValues[len - 1];

    return { q1, median, q3 };
};

// Calculate IQR (Interquartile Range)
const calculateIQR = (q1: number, q3: number): number => {
    return q3 - q1;
};

// Identify outliers (values beyond 1.5 * IQR from Q1 or Q3)
const identifyOutliers = (values: number[], q1: number, q3: number, iqr: number): number[] => {
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    return values.filter(v => v < lowerBound || v > upperBound);
};

// Calculate all box plot statistics
const calculateBoxPlotStats = (values: number[]): BoxPlotStats => {
    if (values.length === 0) {
        return { min: 0, q1: 0, median: 0, q3: 0, max: 0, outliers: [] };
    }

    const sortedValues = [...values].sort((a, b) => a - b);
    const { q1, median, q3 } = calculateQuartiles(sortedValues);
    const iqr = calculateIQR(q1, q3);
    const outliers = identifyOutliers(sortedValues, q1, q3, iqr);

    // Min and max excluding outliers
    const nonOutlierValues = sortedValues.filter(v => !outliers.includes(v));
    const min = nonOutlierValues.length > 0 ? nonOutlierValues[0] : q1;
    const max = nonOutlierValues.length > 0 ? nonOutlierValues[nonOutlierValues.length - 1] : q3;

    return { min, q1, median, q3, max, outliers };
};

export const isBoxPlotData = (columns: any[], dataSource: any[]): boolean => {
    if (!columns || !dataSource || dataSource.length === 0) {
        return false;
    }

    const columnTitles = columns.map(col => (col.title || "").trim());
    const requiredColumns = ["box_category", "box_value", "box_color", "box_title", "box_x_axis_label", "box_y_axis_label"];
    const requiredGroupedColumns = [...requiredColumns, "box_series"];

    const hasAllRegularColumns = requiredColumns.every(reqCol => columnTitles.includes(reqCol));
    const hasAllGroupedColumns = requiredGroupedColumns.every(reqCol => columnTitles.includes(reqCol));

    // Check for exact column count for either regular or grouped
    if (columns.length === 6 && hasAllRegularColumns) return true;
    if (columns.length === 7 && hasAllGroupedColumns) return true;

    return false;
};

export const extractBoxPlotData = (columns: any[], dataSource: any[]): BoxPlotData | null => {
    if (!isBoxPlotData(columns, dataSource)) {
        return null;
    }

    const categoryCol = columns.find(col => (col.title || "").trim() === "box_category");
    const valueCol = columns.find(col => (col.title || "").trim() === "box_value");
    const seriesCol = columns.find(col => (col.title || "").trim() === "box_series");
    const colorCol = columns.find(col => (col.title || "").trim() === "box_color");
    const titleCol = columns.find(col => (col.title || "").trim() === "box_title");
    const xAxisLabelCol = columns.find(col => (col.title || "").trim() === "box_x_axis_label");
    const yAxisLabelCol = columns.find(col => (col.title || "").trim() === "box_y_axis_label");

    if (!categoryCol || !valueCol || !colorCol || !titleCol || !xAxisLabelCol || !yAxisLabelCol) {
        return null;
    }

    const title = String(dataSource[0]?.[titleCol.dataIndex] || "").trim();
    const xAxisLabel = String(dataSource[0]?.[xAxisLabelCol.dataIndex] || "").trim();
    const yAxisLabel = String(dataSource[0]?.[yAxisLabelCol.dataIndex] || "").trim();
    const isGrouped = !!seriesCol;

    if (isGrouped && seriesCol) {
        // Grouped box plot
        const seriesMap = new Map<string, { name: string; color: string; categoryValues: Map<string, number[]> }>();
        const categorySet = new Set<string>();

        dataSource.forEach(row => {
            const category = String(row[categoryCol.dataIndex] || "").trim();
            const valueStr = String(row[valueCol.dataIndex] || "0").replace(/,/g, "");
            const value = parseFloat(valueStr) || 0;
            const seriesName = String(row[seriesCol.dataIndex] || "").trim();
            const color = String(row[colorCol.dataIndex] || "#000000").trim();

            categorySet.add(category);

            if (!seriesMap.has(seriesName)) {
                seriesMap.set(seriesName, {
                    name: seriesName,
                    color: color,
                    categoryValues: new Map()
                });
            }

            const seriesData = seriesMap.get(seriesName)!;
            if (!seriesData.categoryValues.has(category)) {
                seriesData.categoryValues.set(category, []);
            }
            seriesData.categoryValues.get(category)!.push(value);
        });

        const categories = Array.from(categorySet).sort();
        const series = Array.from(seriesMap.values()).map(s => ({
            name: s.name,
            color: s.color,
            boxes: categories.map(category => {
                const values = s.categoryValues.get(category) || [];
                return {
                    category,
                    values,
                    stats: calculateBoxPlotStats(values)
                };
            })
        }));

        return {
            title,
            xAxisLabel: xAxisLabel || undefined,
            yAxisLabel: yAxisLabel || undefined,
            isGrouped: true,
            series
        };
    } else {
        // Regular box plot
        const categoryMap = new Map<string, { color: string; values: number[] }>();

        dataSource.forEach(row => {
            const category = String(row[categoryCol.dataIndex] || "").trim();
            const valueStr = String(row[valueCol.dataIndex] || "0").replace(/,/g, "");
            const value = parseFloat(valueStr) || 0;
            const color = String(row[colorCol.dataIndex] || "#000000").trim();

            if (!categoryMap.has(category)) {
                categoryMap.set(category, {
                    color: color,
                    values: []
                });
            }
            categoryMap.get(category)!.values.push(value);
        });

        const categories = Array.from(categoryMap.keys()).sort();
        const boxes = categories.map(category => {
            const data = categoryMap.get(category)!;
            return {
                category,
                color: data.color,
                values: data.values,
                stats: calculateBoxPlotStats(data.values)
            };
        });

        return {
            title,
            xAxisLabel: xAxisLabel || undefined,
            yAxisLabel: yAxisLabel || undefined,
            isGrouped: false,
            boxes
        };
    }
};

export function BoxPlot(props: {
    data: BoxPlotData;
    darkMode: boolean;
    jobAlias?: string;
    onDownloadCsv?: () => void;
    onDownloadPng?: () => void;
    onAddQuery?: () => void;
}) {
    const { data, darkMode, onDownloadCsv, onDownloadPng, onAddQuery } = props;
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
    const [pressedCsv, setPressedCsv] = useState(false);
    const [pressedPng, setPressedPng] = useState(false);
    const [isAddQueryHovered, setIsAddQueryHovered] = useState(false);
    const [pressedAddQuery, setPressedAddQuery] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return;

        let currentWidth = 0;
        let currentHeight = 0;
        let isInitialized = false;

        const updateDimensions = (width: number) => {
            if (width > 0) {
                const chartWidth = Math.min(width - 40, 1200);
                const chartHeight = Math.min(chartWidth * 0.625, 600);

                // Only update if dimensions actually changed
                if (chartWidth !== currentWidth || chartHeight !== currentHeight) {
                    currentWidth = chartWidth;
                    currentHeight = chartHeight;
                    setDimensions({ width: chartWidth, height: chartHeight });
                }
            }
        };

        // Initial dimensions - wait for next frame to ensure layout is complete
        requestAnimationFrame(() => {
            if (!containerRef.current) return;
            const initialWidth = containerRef.current.clientWidth || containerRef.current.offsetWidth;
            if (initialWidth > 0) {
                updateDimensions(initialWidth);
                isInitialized = true;
            }
        });

        // Observe container for size changes with a small debounce
        let resizeTimeout: NodeJS.Timeout;
        const resizeObserver = new ResizeObserver(entries => {
            if (!isInitialized) return; // Ignore resize events until initial dimensions are set
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                for (const entry of entries) {
                    const { width } = entry.contentRect;
                    if (width > 0) {
                        updateDimensions(width);
                    }
                }
            }, 10);
        });

        // Delay observer setup slightly to avoid immediate fire
        const setupTimeout = setTimeout(() => {
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
        }, 50);

        return () => {
            clearTimeout(resizeTimeout);
            clearTimeout(setupTimeout);
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!svgRef.current) return;
        if (!data.isGrouped && (!data.boxes || data.boxes.length === 0)) return;
        if (data.isGrouped && (!data.series || data.series.length === 0)) return;

        const svg = select(svgRef.current);
        svg.selectAll("*").remove();

        const margin = { top: 40, right: data.isGrouped ? 200 : 40, bottom: 120, left: 80 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Set background to transparent
        svg.append("rect")
            .attr("width", dimensions.width)
            .attr("height", dimensions.height)
            .attr("fill", "transparent");

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        if (data.isGrouped && data.series) {
            // Grouped box plot rendering
            const allCategories = Array.from(new Set(data.series.flatMap(s => s.boxes.map(b => b.category)))).sort();
            const seriesNames = data.series.map(s => s.name);

            // Calculate Y-axis domain from all stats
            let yMin = Infinity;
            let yMax = -Infinity;
            data.series.forEach(series => {
                series.boxes.forEach(box => {
                    const stats = box.stats;
                    const allValues = [stats.min, stats.q1, stats.median, stats.q3, stats.max, ...stats.outliers];
                    yMin = Math.min(yMin, ...allValues);
                    yMax = Math.max(yMax, ...allValues);
                });
            });

            const yScale = scaleLinear()
                .domain([yMin - (yMax - yMin) * 0.1, yMax + (yMax - yMin) * 0.1])
                .range([height, 0]);

            // Outer scale for categories
            const xScale = scaleBand()
                .domain(allCategories)
                .range([0, width])
                .padding(0.2);

            // Inner scale for series within each category
            const xSubScale = scaleBand()
                .domain(seriesNames)
                .range([0, xScale.bandwidth()])
                .padding(0.1);

            // Y-axis
            g.append("g")
                .call(axisLeft(yScale))
                .selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000");

            // Axis lines
            g.selectAll(".domain, .tick line")
                .attr("stroke", darkMode ? "#666666" : "#cccccc");

            // Y-axis label
            if (data.yAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", -margin.left + 20)
                    .attr("x", -height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(data.yAxisLabel);
            }

            // X-axis with horizontal labels
            const xAxis = g.append("g")
                .attr("transform", `translate(0,${height})`)
                .call(axisBottom(xScale));

            xAxis.selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("transform", null)
                .style("font-size", "12px")
                .style("text-anchor", "middle");

            // Render box plots for each series
            data.series.forEach(seriesData => {
                const seriesGroup = g.append("g")
                    .attr("class", `series-${seriesData.name.replace(/\s+/g, '-')}`);

                seriesData.boxes.forEach(box => {
                    const stats = box.stats;
                    const categoryX = xScale(box.category) || 0;
                    const seriesX = xSubScale(seriesData.name) || 0;
                    const boxX = categoryX + seriesX;
                    const boxWidth = xSubScale.bandwidth();

                    // Box (Q1 to Q3) with tooltips on edges
                    const boxRect = seriesGroup.append("rect")
                        .attr("x", boxX)
                        .attr("y", yScale(stats.q3))
                        .attr("width", boxWidth)
                        .attr("height", yScale(stats.q1) - yScale(stats.q3))
                        .attr("fill", seriesData.color)
                        .attr("fill-opacity", 0.7)
                        .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                        .attr("stroke-width", 1);

                    // Q1 edge tooltip (bottom of box)
                    boxRect.clone()
                        .attr("y", yScale(stats.q1) - 1)
                        .attr("height", 1)
                        .attr("fill", "transparent")
                        .attr("stroke", "none")
                        .on("mouseover", function(this: SVGRectElement) {
                            const tooltipText = `Q1: ${stats.q1.toLocaleString()}`;
                            const textLength = tooltipText.length * 6;
                            const tooltipWidth = Math.max(textLength + 20, 80);

                            const tooltip = g.append("g")
                                .attr("class", "tooltip")
                                .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.q1) - 15})`);

                            tooltip.append("rect")
                                .attr("x", -tooltipWidth / 2)
                                .attr("y", -12)
                                .attr("width", tooltipWidth)
                                .attr("height", 20)
                                .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                .attr("rx", 4);

                            tooltip.append("text")
                                .attr("text-anchor", "middle")
                                .attr("fill", darkMode ? "#ffffff" : "#000000")
                                .attr("font-size", "11px")
                                .attr("y", 2)
                                .text(tooltipText);
                        })
                        .on("mouseout", function() {
                            g.selectAll(".tooltip").remove();
                        });

                    // Q3 edge tooltip (top of box)
                    boxRect.clone()
                        .attr("y", yScale(stats.q3))
                        .attr("height", 1)
                        .attr("fill", "transparent")
                        .attr("stroke", "none")
                        .on("mouseover", function(this: SVGRectElement) {
                            const tooltipText = `Q3: ${stats.q3.toLocaleString()}`;
                            const textLength = tooltipText.length * 6;
                            const tooltipWidth = Math.max(textLength + 20, 80);

                            const tooltip = g.append("g")
                                .attr("class", "tooltip")
                                .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.q3) - 15})`);

                            tooltip.append("rect")
                                .attr("x", -tooltipWidth / 2)
                                .attr("y", -12)
                                .attr("width", tooltipWidth)
                                .attr("height", 20)
                                .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                .attr("rx", 4);

                            tooltip.append("text")
                                .attr("text-anchor", "middle")
                                .attr("fill", darkMode ? "#ffffff" : "#000000")
                                .attr("font-size", "11px")
                                .attr("y", 2)
                                .text(tooltipText);
                        })
                        .on("mouseout", function() {
                            g.selectAll(".tooltip").remove();
                        });

                    // Median line with tooltip
                    seriesGroup.append("line")
                        .attr("x1", boxX)
                        .attr("x2", boxX + boxWidth)
                        .attr("y1", yScale(stats.median))
                        .attr("y2", yScale(stats.median))
                        .attr("stroke", darkMode ? "#ffffff" : "#000000")
                        .attr("stroke-width", 2)
                        .on("mouseover", function(this: SVGLineElement) {
                            select(this)
                                .attr("stroke-width", 3);

                            const tooltipText = `Median: ${stats.median.toLocaleString()}`;
                            const textLength = tooltipText.length * 6;
                            const tooltipWidth = Math.max(textLength + 20, 80);

                            const tooltip = g.append("g")
                                .attr("class", "tooltip")
                                .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.median) - 15})`);

                            tooltip.append("rect")
                                .attr("x", -tooltipWidth / 2)
                                .attr("y", -12)
                                .attr("width", tooltipWidth)
                                .attr("height", 20)
                                .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                .attr("rx", 4);

                            tooltip.append("text")
                                .attr("text-anchor", "middle")
                                .attr("fill", darkMode ? "#ffffff" : "#000000")
                                .attr("font-size", "11px")
                                .attr("y", 2)
                                .text(tooltipText);
                        })
                        .on("mouseout", function(this: SVGLineElement) {
                            select(this)
                                .attr("stroke-width", 2);
                            g.selectAll(".tooltip").remove();
                        });

                    // Lower whisker (min to Q1)
                    seriesGroup.append("line")
                        .attr("x1", boxX + boxWidth / 2)
                        .attr("x2", boxX + boxWidth / 2)
                        .attr("y1", yScale(stats.min))
                        .attr("y2", yScale(stats.q1))
                        .attr("stroke", darkMode ? "#ffffff" : "#000000")
                        .attr("stroke-width", 1);

                    // Upper whisker (Q3 to max)
                    seriesGroup.append("line")
                        .attr("x1", boxX + boxWidth / 2)
                        .attr("x2", boxX + boxWidth / 2)
                        .attr("y1", yScale(stats.q3))
                        .attr("y2", yScale(stats.max))
                        .attr("stroke", darkMode ? "#ffffff" : "#000000")
                        .attr("stroke-width", 1);

                    // Whisker caps with tooltips
                    const capWidth = boxWidth * 0.3;
                    seriesGroup.append("line")
                        .attr("x1", boxX + boxWidth / 2 - capWidth / 2)
                        .attr("x2", boxX + boxWidth / 2 + capWidth / 2)
                        .attr("y1", yScale(stats.min))
                        .attr("y2", yScale(stats.min))
                        .attr("stroke", darkMode ? "#ffffff" : "#000000")
                        .attr("stroke-width", 1)
                        .on("mouseover", function(this: SVGLineElement) {
                            select(this)
                                .attr("stroke-width", 2);

                            const tooltipText = `Min: ${stats.min.toLocaleString()}`;
                            const textLength = tooltipText.length * 6;
                            const tooltipWidth = Math.max(textLength + 20, 80);

                            const tooltip = g.append("g")
                                .attr("class", "tooltip")
                                .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.min) - 15})`);

                            tooltip.append("rect")
                                .attr("x", -tooltipWidth / 2)
                                .attr("y", -12)
                                .attr("width", tooltipWidth)
                                .attr("height", 20)
                                .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                .attr("rx", 4);

                            tooltip.append("text")
                                .attr("text-anchor", "middle")
                                .attr("fill", darkMode ? "#ffffff" : "#000000")
                                .attr("font-size", "11px")
                                .attr("y", 2)
                                .text(tooltipText);
                        })
                        .on("mouseout", function(this: SVGLineElement) {
                            select(this)
                                .attr("stroke-width", 1);
                            g.selectAll(".tooltip").remove();
                        });

                    seriesGroup.append("line")
                        .attr("x1", boxX + boxWidth / 2 - capWidth / 2)
                        .attr("x2", boxX + boxWidth / 2 + capWidth / 2)
                        .attr("y1", yScale(stats.max))
                        .attr("y2", yScale(stats.max))
                        .attr("stroke", darkMode ? "#ffffff" : "#000000")
                        .attr("stroke-width", 1)
                        .on("mouseover", function(this: SVGLineElement) {
                            select(this)
                                .attr("stroke-width", 2);

                            const tooltipText = `Max: ${stats.max.toLocaleString()}`;
                            const textLength = tooltipText.length * 6;
                            const tooltipWidth = Math.max(textLength + 20, 80);

                            const tooltip = g.append("g")
                                .attr("class", "tooltip")
                                .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.max) - 15})`);

                            tooltip.append("rect")
                                .attr("x", -tooltipWidth / 2)
                                .attr("y", -12)
                                .attr("width", tooltipWidth)
                                .attr("height", 20)
                                .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                .attr("rx", 4);

                            tooltip.append("text")
                                .attr("text-anchor", "middle")
                                .attr("fill", darkMode ? "#ffffff" : "#000000")
                                .attr("font-size", "11px")
                                .attr("y", 2)
                                .text(tooltipText);
                        })
                        .on("mouseout", function(this: SVGLineElement) {
                            select(this)
                                .attr("stroke-width", 1);
                            g.selectAll(".tooltip").remove();
                        });

                    // Outliers with tooltips
                    stats.outliers.forEach(outlier => {
                        seriesGroup.append("circle")
                            .attr("cx", boxX + boxWidth / 2)
                            .attr("cy", yScale(outlier))
                            .attr("r", 3)
                            .attr("fill", seriesData.color)
                            .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                            .attr("stroke-width", 1)
                            .on("mouseover", function(this: SVGCircleElement, event: MouseEvent) {
                                select(this)
                                    .attr("opacity", 0.7)
                                    .attr("r", 5);

                                // Show tooltip
                                const tooltipText = `Outlier: ${outlier.toLocaleString()}`;
                                const textLength = tooltipText.length * 6;
                                const tooltipWidth = Math.max(textLength + 20, 80);

                                const tooltip = g.append("g")
                                    .attr("class", "tooltip")
                                    .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(outlier) - 15})`);

                                tooltip.append("rect")
                                    .attr("x", -tooltipWidth / 2)
                                    .attr("y", -12)
                                    .attr("width", tooltipWidth)
                                    .attr("height", 20)
                                    .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                    .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                    .attr("rx", 4);

                                tooltip.append("text")
                                    .attr("text-anchor", "middle")
                                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                                    .attr("font-size", "11px")
                                    .attr("y", 2)
                                    .text(tooltipText);
                            })
                            .on("mouseout", function(this: SVGCircleElement) {
                                select(this)
                                    .attr("opacity", 1)
                                    .attr("r", 3);
                                g.selectAll(".tooltip").remove();
                            });
                    });
                });
            });

            // Legend for grouped box plots
            const legend = svg.append("g")
                .attr("class", "legend")
                .attr("transform", `translate(${dimensions.width - margin.right + 20}, ${margin.top + 20})`);

            const legendItems = legend.selectAll(".legend-item")
                .data(data.series)
                .enter()
                .append("g")
                .attr("class", "legend-item")
                .attr("transform", (_d, i: number) => `translate(0, ${i * 25})`);

            legendItems.append("rect")
                .attr("width", 15)
                .attr("height", 15)
                .attr("fill", d => d.color)
                .attr("fill-opacity", 0.7);

            legendItems.append("text")
                .attr("x", 20)
                .attr("y", 12)
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .text(d => d.name);

        } else if (data.boxes) {
            // Regular box plot rendering
            // Calculate Y-axis domain from all stats
            let yMin = Infinity;
            let yMax = -Infinity;
            data.boxes.forEach(box => {
                const stats = box.stats;
                const allValues = [stats.min, stats.q1, stats.median, stats.q3, stats.max, ...stats.outliers];
                yMin = Math.min(yMin, ...allValues);
                yMax = Math.max(yMax, ...allValues);
            });

            const yScale = scaleLinear()
                .domain([yMin - (yMax - yMin) * 0.1, yMax + (yMax - yMin) * 0.1])
                .range([height, 0]);

            const xScale = scaleBand()
                .domain(data.boxes.map(box => box.category))
                .range([0, width])
                .padding(0.2);

            // Y-axis
            g.append("g")
                .call(axisLeft(yScale))
                .selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000");

            // Axis lines
            g.selectAll(".domain, .tick line")
                .attr("stroke", darkMode ? "#666666" : "#cccccc");

            // Y-axis label
            if (data.yAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", -margin.left + 20)
                    .attr("x", -height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(data.yAxisLabel);
            }

            // X-axis with horizontal labels
            const xAxis = g.append("g")
                .attr("transform", `translate(0,${height})`)
                .call(axisBottom(xScale));

            xAxis.selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("transform", null)
                .style("font-size", "12px")
                .style("text-anchor", "middle");

            // Render box plots
            data.boxes.forEach(box => {
                const stats = box.stats;
                const boxX = xScale(box.category) || 0;
                const boxWidth = xScale.bandwidth();

                // Box (Q1 to Q3) with tooltips on edges
                const boxRect = g.append("rect")
                    .attr("x", boxX)
                    .attr("y", yScale(stats.q3))
                    .attr("width", boxWidth)
                    .attr("height", yScale(stats.q1) - yScale(stats.q3))
                    .attr("fill", box.color)
                    .attr("fill-opacity", 0.7)
                    .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                    .attr("stroke-width", 1);

                // Q1 edge tooltip (bottom of box)
                boxRect.clone()
                    .attr("y", yScale(stats.q1) - 1)
                    .attr("height", 1)
                    .attr("fill", "transparent")
                    .attr("stroke", "none")
                    .on("mouseover", function(this: SVGRectElement) {
                        const tooltipText = `Q1: ${stats.q1.toLocaleString()}`;
                        const textLength = tooltipText.length * 6;
                        const tooltipWidth = Math.max(textLength + 20, 80);

                        const tooltip = g.append("g")
                            .attr("class", "tooltip")
                            .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.q1) - 15})`);

                        tooltip.append("rect")
                            .attr("x", -tooltipWidth / 2)
                            .attr("y", -12)
                            .attr("width", tooltipWidth)
                            .attr("height", 20)
                            .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                            .attr("stroke", darkMode ? "#666666" : "#cccccc")
                            .attr("rx", 4);

                        tooltip.append("text")
                            .attr("text-anchor", "middle")
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "11px")
                            .attr("y", 2)
                            .text(tooltipText);
                    })
                    .on("mouseout", function() {
                        g.selectAll(".tooltip").remove();
                    });

                // Q3 edge tooltip (top of box)
                boxRect.clone()
                    .attr("y", yScale(stats.q3))
                    .attr("height", 1)
                    .attr("fill", "transparent")
                    .attr("stroke", "none")
                    .on("mouseover", function(this: SVGRectElement) {
                        const tooltipText = `Q3: ${stats.q3.toLocaleString()}`;
                        const textLength = tooltipText.length * 6;
                        const tooltipWidth = Math.max(textLength + 20, 80);

                        const tooltip = g.append("g")
                            .attr("class", "tooltip")
                            .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.q3) - 15})`);

                        tooltip.append("rect")
                            .attr("x", -tooltipWidth / 2)
                            .attr("y", -12)
                            .attr("width", tooltipWidth)
                            .attr("height", 20)
                            .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                            .attr("stroke", darkMode ? "#666666" : "#cccccc")
                            .attr("rx", 4);

                        tooltip.append("text")
                            .attr("text-anchor", "middle")
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "11px")
                            .attr("y", 2)
                            .text(tooltipText);
                    })
                    .on("mouseout", function() {
                        g.selectAll(".tooltip").remove();
                    });

                // Median line with tooltip
                g.append("line")
                    .attr("x1", boxX)
                    .attr("x2", boxX + boxWidth)
                    .attr("y1", yScale(stats.median))
                    .attr("y2", yScale(stats.median))
                    .attr("stroke", darkMode ? "#ffffff" : "#000000")
                    .attr("stroke-width", 2)
                    .on("mouseover", function(this: SVGLineElement) {
                        select(this)
                            .attr("stroke-width", 3);

                        const tooltipText = `Median: ${stats.median.toLocaleString()}`;
                        const textLength = tooltipText.length * 6;
                        const tooltipWidth = Math.max(textLength + 20, 80);

                        const tooltip = g.append("g")
                            .attr("class", "tooltip")
                            .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.median) - 15})`);

                        tooltip.append("rect")
                            .attr("x", -tooltipWidth / 2)
                            .attr("y", -12)
                            .attr("width", tooltipWidth)
                            .attr("height", 20)
                            .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                            .attr("stroke", darkMode ? "#666666" : "#cccccc")
                            .attr("rx", 4);

                        tooltip.append("text")
                            .attr("text-anchor", "middle")
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "11px")
                            .attr("y", 2)
                            .text(tooltipText);
                    })
                    .on("mouseout", function(this: SVGLineElement) {
                        select(this)
                            .attr("stroke-width", 2);
                        g.selectAll(".tooltip").remove();
                    });

                // Lower whisker (min to Q1)
                g.append("line")
                    .attr("x1", boxX + boxWidth / 2)
                    .attr("x2", boxX + boxWidth / 2)
                    .attr("y1", yScale(stats.min))
                    .attr("y2", yScale(stats.q1))
                    .attr("stroke", darkMode ? "#ffffff" : "#000000")
                    .attr("stroke-width", 1);

                // Upper whisker (Q3 to max)
                g.append("line")
                    .attr("x1", boxX + boxWidth / 2)
                    .attr("x2", boxX + boxWidth / 2)
                    .attr("y1", yScale(stats.q3))
                    .attr("y2", yScale(stats.max))
                    .attr("stroke", darkMode ? "#ffffff" : "#000000")
                    .attr("stroke-width", 1);

                // Whisker caps with tooltips
                const capWidth = boxWidth * 0.3;
                g.append("line")
                    .attr("x1", boxX + boxWidth / 2 - capWidth / 2)
                    .attr("x2", boxX + boxWidth / 2 + capWidth / 2)
                    .attr("y1", yScale(stats.min))
                    .attr("y2", yScale(stats.min))
                    .attr("stroke", darkMode ? "#ffffff" : "#000000")
                    .attr("stroke-width", 1)
                    .on("mouseover", function(this: SVGLineElement) {
                        select(this)
                            .attr("stroke-width", 2);

                        const tooltipText = `Min: ${stats.min.toLocaleString()}`;
                        const textLength = tooltipText.length * 6;
                        const tooltipWidth = Math.max(textLength + 20, 80);

                        const tooltip = g.append("g")
                            .attr("class", "tooltip")
                            .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.min) - 15})`);

                        tooltip.append("rect")
                            .attr("x", -tooltipWidth / 2)
                            .attr("y", -12)
                            .attr("width", tooltipWidth)
                            .attr("height", 20)
                            .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                            .attr("stroke", darkMode ? "#666666" : "#cccccc")
                            .attr("rx", 4);

                        tooltip.append("text")
                            .attr("text-anchor", "middle")
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "11px")
                            .attr("y", 2)
                            .text(tooltipText);
                    })
                    .on("mouseout", function(this: SVGLineElement) {
                        select(this)
                            .attr("stroke-width", 1);
                        g.selectAll(".tooltip").remove();
                    });

                g.append("line")
                    .attr("x1", boxX + boxWidth / 2 - capWidth / 2)
                    .attr("x2", boxX + boxWidth / 2 + capWidth / 2)
                    .attr("y1", yScale(stats.max))
                    .attr("y2", yScale(stats.max))
                    .attr("stroke", darkMode ? "#ffffff" : "#000000")
                    .attr("stroke-width", 1)
                    .on("mouseover", function(this: SVGLineElement) {
                        select(this)
                            .attr("stroke-width", 2);

                        const tooltipText = `Max: ${stats.max.toLocaleString()}`;
                        const textLength = tooltipText.length * 6;
                        const tooltipWidth = Math.max(textLength + 20, 80);

                        const tooltip = g.append("g")
                            .attr("class", "tooltip")
                            .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(stats.max) - 15})`);

                        tooltip.append("rect")
                            .attr("x", -tooltipWidth / 2)
                            .attr("y", -12)
                            .attr("width", tooltipWidth)
                            .attr("height", 20)
                            .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                            .attr("stroke", darkMode ? "#666666" : "#cccccc")
                            .attr("rx", 4);

                        tooltip.append("text")
                            .attr("text-anchor", "middle")
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "11px")
                            .attr("y", 2)
                            .text(tooltipText);
                    })
                    .on("mouseout", function(this: SVGLineElement) {
                        select(this)
                            .attr("stroke-width", 1);
                        g.selectAll(".tooltip").remove();
                    });

                // Outliers with tooltips
                stats.outliers.forEach(outlier => {
                    g.append("circle")
                        .attr("cx", boxX + boxWidth / 2)
                        .attr("cy", yScale(outlier))
                        .attr("r", 3)
                        .attr("fill", box.color)
                        .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                        .attr("stroke-width", 1)
                        .on("mouseover", function(this: SVGCircleElement, event: MouseEvent) {
                            select(this)
                                .attr("opacity", 0.7)
                                .attr("r", 5);

                            // Show tooltip
                            const tooltipText = `Outlier: ${outlier.toLocaleString()}`;
                            const textLength = tooltipText.length * 6;
                            const tooltipWidth = Math.max(textLength + 20, 80);

                            const tooltip = g.append("g")
                                .attr("class", "tooltip")
                                .attr("transform", `translate(${boxX + boxWidth / 2},${yScale(outlier) - 15})`);

                            tooltip.append("rect")
                                .attr("x", -tooltipWidth / 2)
                                .attr("y", -12)
                                .attr("width", tooltipWidth)
                                .attr("height", 20)
                                .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                .attr("rx", 4);

                            tooltip.append("text")
                                .attr("text-anchor", "middle")
                                .attr("fill", darkMode ? "#ffffff" : "#000000")
                                .attr("font-size", "11px")
                                .attr("y", 2)
                                .text(tooltipText);
                        })
                        .on("mouseout", function(this: SVGCircleElement) {
                            select(this)
                                .attr("opacity", 1)
                                .attr("r", 3);
                            g.selectAll(".tooltip").remove();
                        });
                });
            });
        }

        // X-axis label
        if (data.xAxisLabel) {
            g.append("text")
                .attr("x", width / 2)
                .attr("y", height + margin.bottom - 10)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "14px")
                .text(data.xAxisLabel);
        }

    }, [data, dimensions, darkMode]);

    const handleDownloadCsv = () => {
        setPressedCsv(true);
        setTimeout(() => setPressedCsv(false), 200);
        if (onDownloadCsv) {
            onDownloadCsv();
        }
    };

    const handleDownloadPng = async () => {
        if (!svgRef.current) return;

        setPressedPng(true);
        setTimeout(() => setPressedPng(false), 200);

        if (onDownloadPng) {
            onDownloadPng();
        }

        try {
            const svg = svgRef.current.cloneNode(true) as SVGElement;

            // Set SVG dimensions
            svg.setAttribute('width', dimensions.width.toString());
            svg.setAttribute('height', dimensions.height.toString());
            svg.setAttribute('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`);

            const svgData = new XMLSerializer().serializeToString(svg);

            // Create a data URL with proper namespace
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            // Scale factor for higher resolution (2x for retina quality)
            const scale = 2;

            img.onload = () => {
                // Set canvas size with scale factor for higher resolution
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                if (ctx) {
                    // Scale the context for high DPI
                    ctx.scale(scale, scale);

                    // Fill background (transparent)
                    ctx.fillStyle = 'transparent';
                    ctx.fillRect(0, 0, img.width, img.height);

                    // Draw the SVG image
                    ctx.drawImage(img, 0, 0);

                    // Convert to blob and download
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.setAttribute('href', blobUrl);
                            const filename = `${data.title.replace(/[<>:"/\\|?*]/g, '')}.png`;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(blobUrl);
                        }
                    }, 'image/png', 1.0); // Maximum quality
                }
                URL.revokeObjectURL(url);
            };

            img.onerror = () => {
                console.error('Error loading SVG image');
                URL.revokeObjectURL(url);
                notification.error({
                    message: 'Failed to download PNG',
                    description: 'An error occurred while generating the PNG image.',
                    placement: 'bottomLeft',
                    duration: 3
                });
            };

            img.src = url;
        } catch (error) {
            console.error('Error downloading PNG:', error);
            notification.error({
                message: 'Failed to download PNG',
                description: 'An error occurred while generating the PNG image.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    return (
        <div ref={containerRef} style={{ marginBottom: "1em", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: "100%" }}>
            <h3 style={{
                color: darkMode ? "#ffffff" : "#000000",
                marginBottom: "1em",
                textAlign: "center"
            }}>
                {data.title}
            </h3>
            <div style={{ position: "relative", display: "inline-block" }}>
                <svg
                    ref={svgRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    style={{ maxWidth: "100%", overflow: "visible" }}
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                />
                <div style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    display: "flex",
                    gap: "0.75em"
                }}>
                    <ConditionalTooltip title="Download box plot as PNG">
                        <button
                            onClick={handleDownloadPng}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedPng ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <FileImageOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Download box plot data as CSV">
                        <button
                            onClick={handleDownloadCsv}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedCsv ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <TableOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Add query">
                        <button
                            onClick={() => {
                                setPressedAddQuery(true);
                                setTimeout(() => setPressedAddQuery(false), 200);
                                onAddQuery?.();
                            }}
                            onMouseEnter={() => setIsAddQueryHovered(true)}
                            onMouseLeave={() => setIsAddQueryHovered(false)}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: isAddQueryHovered
                                    ? (darkMode ? "#ffffff" : "#000000")
                                    : (darkMode ? "#888888" : "#AFAFAF"),
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedAddQuery ? "scale(0.85)" : "scale(1)"
                            }}
                        >
                            <DatabaseOutlined />
                        </button>
                    </ConditionalTooltip>
                </div>
            </div>
        </div>
    );
}

